import { useCallback, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useCustomFields } from "../../../../Settings/CustomFields/context";
import { useCompanyCurrency, useCompanyDateFormat, useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { useAccess } from "../../../../../utils/hooks/access";
import { getTimeSheetTotal } from "../../TimeSheetTable/useColumns/useTotalColumn";
import { getTimeSheetDuration } from "../../TimeSheetTable/useColumns/useDurationColumn";
import { getHourTypeColor } from "../../../../../utils/helpers/hourType";
import { renderOrgLevelResourceLabel, renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";
import { renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../utils/helpers/currencyFormatter";

const columnHelper = createColumnHelper();

export const useSubTableColumns = ({ groupBy, overtimeDrawer = false }) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const currency = useCompanyCurrency();
    const { groups } = useGroups();
    const { timesheetCustomFields } = useCustomFields();
    const { hasAccess: canViewRates } = useAccess("canViewRates");

    const getPayCodeStyle = useCallback((value) => {
        if (value) {
            return {
                backgroundColor: getHourTypeColor(value?.hourType),
                color: value?.hourType === "unpaid" ? "#00080e" : "white",
                minWidth: "60px",
                display: "inline-block",
            };
        }
        return {};
    }, []);

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("date", {
                header: t("date"),
                meta: { rowSpanBy: "date" },
                cell: (info) => {
                    const value = info.getValue();
                    return value && (
                        <span className="text-uppercase pl-2">
                            {moment(value).format(`${dateFormat} ddd`)}
                        </span>
                    );
                }
            }),
            columnHelper.accessor("startDate", {
                header: t("in"),
                cell: (info) => {
                    const value = info.getValue();
                    const original = info?.row?.original;
                    return value && !['amount', 'unit'].includes(original?.hourType) ? (
                        <span
                            className="p-1 rounded"
                            style={{
                                background: "rgba(0, 153, 139, 0.1)",
                                color: "#00998b"
                            }}
                        >
                            {moment.parseZone(value).format(timeFormat)}
                        </span>
                    ) : "-";
                }
            }),
            columnHelper.accessor("endDate", {
                header: t("out"),
                cell: (info) => {
                    const original = info?.row?.original;
                    const value = info.getValue();
                    return value && !['amount', 'unit'].includes(original?.hourType) ? (
                        <span
                            className="p-1 rounded"
                            style={{
                                background: "rgba(255, 97, 97, 0.1)",
                                color: "#ff6161"
                            }}
                        >
                            {moment.parseZone(value).format(timeFormat)}
                        </span>
                    ) : "-";
                }
            }),
            columnHelper.accessor("overnight", {
                header: t("on"),
                cell: ({ row: { original } }) => {
                    const { startDate, endDate } = original;
                    let duration = 0;
                    if (startDate && endDate) {
                        const parsedStartDate = moment.parseZone(startDate);
                        const parsedEndDate = moment.parseZone(endDate);
                        if (parsedEndDate.date() !== parsedStartDate.date()) {
                            duration = parsedEndDate.date() - parsedStartDate.date();
                        }
                    }
                    return duration > 0 ? `+${duration}` : "-";
                }
            }),
            columnHelper.accessor("payCode", {
                header: t("time-code"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? (
                        <span className="px-2 py-1 text-center rounded" style={getPayCodeStyle(value)}>
                            {value?.code}
                        </span>
                    ) : null;
                }
            }),
            columnHelper.accessor("payCode.description", {
                header: t("time-code-description")
            }),
            columnHelper.accessor("payCode.hourType", {
                header: t("hour-type"),
                cell: (info) => {
                    const value = info.getValue();
                    return t(value);
                }
            }),
            columnHelper.accessor("duration", {
                header: t("duration"),
                cell: (info) => {
                    const value = info.getValue();
                    const original = info?.row?.original;
                    return value && !['amount'].includes(original?.hourType) ? renderDurationAsFormat(value || 0, "HH:mm", true, true) : "-";
                }
            }),
            columnHelper.accessor("otherDuration", {
                header: t("premium"),
                cell: (info) => {
                    const original = info?.row?.original;
                    const value = info.getValue();
                    return value && !['amount'].includes(original?.hourType) ? renderDurationAsFormat(value, "HH:mm", true, true) : "-";
                }
            }),
            columnHelper.display({
                id: "date-hours",
                header: t("paid-hours"),
                meta: { rowSpanBy: "date" },
                cell: ({ row: { original }, table }) => {
                    const data = table.options.data;
                    const duration = data
                        .filter((timeSheet) => {
                            if (timeSheet.hourType === "unpaid") return false;
                            if (timeSheet.payCode.excludeFromPaidHours) return false;
                            return moment(timeSheet.date).isSame(moment(original.date), "date");
                        })
                        .reduce((total, timeSheet) => total + getTimeSheetDuration(timeSheet), 0);

                    return (
                        <div className="font-weight-bold text-start">
                            {renderDurationAsFormat(duration, timeFormat)}
                        </div>
                    );
                },
            }),
            columnHelper.accessor("location", {
                header: t("location"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? renderSettingsResourceLabel(value) : null;
                }
            }),
            columnHelper.accessor("job", {
                header: t("job"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? renderSettingsResourceLabel(value) : null;
                }
            })
        ];

        if (groups?.length > 0) {
            const groupColumns = groups.map((group) =>
                columnHelper.accessor(`group.${group.level.replaceAll("_", "")}`, {
                    header: group.description,
                    cell: ({ row: { original } }) => {
                        const value = original?.[`group.${group.level.replaceAll("_", "")}`];
                        return value ? renderOrgLevelResourceLabel(value) : null;
                    }
                }));
            columns.push(...groupColumns);
        }

        if (timesheetCustomFields?.length > 0) {
            const customFieldsColumns = timesheetCustomFields.map((item) =>
                columnHelper.accessor(`customFields.${item.code}`, {
                    header: item.description,
                    cell: (info) => {
                        const value = info.getValue();
                        return value ? renderOrgLevelResourceLabel(value) : null;
                    }
                }));
            columns.push(...customFieldsColumns);
        }

        if (!groupBy && !overtimeDrawer) {
            columns.splice(1, 0, columnHelper.accessor("employeeNumber", {
                header: t("employee-number"),
                cell: ({ row: { original } }) => original?.user?.employeeNumber
            }));
        }

        if (!groupBy || overtimeDrawer) {
            columns.splice(10, 0, columnHelper.accessor("primaryCompany", {
                header: t("company"),
                cell: ({ row: { original } }) => {
                    const value = original?.user?.primaryCompany;
                    return value ? value?.name : null;
                }
            }));
        }

        if (canViewRates) {
            columns.splice(3, 0, columnHelper.accessor("rate", {
                header: t("rate"),
                cell: ({ row: { original } }) => {
                    return currencyFormatter(original.rate, 2, original.job.currency || currency);
                }
            }));

            columns.splice(8, 0, columnHelper.accessor("total", {
                header: t("total"),
                cell: ({ row: { original } }) => {
                    return currencyFormatter(original?.total || 0, 2, original?.job?.currency || currency);
                }
            }));

            columns.splice(12, 0, columnHelper.display({
                id: "date-total",
                header: t("paid-wages"),
                meta: { rowSpanBy: "date" },
                cell: ({ row: { original }, table }) => {
                    const data = table.options.data;
                    const total = data
                        .filter((item) => moment(item.date).isSame(moment(original.date), "date"))
                        .reduce((total, timeSheet) => {
                            return total + getTimeSheetTotal(timeSheet);
                        }, 0);

                    return (
                        <div className="font-weight-bold text-start">
                            {currencyFormatter(total || 0, 2, original.job.currency || currency)}
                        </div>
                    );
                },
            }))
        }

        return columns;
    }, [t, groups, timesheetCustomFields, groupBy, overtimeDrawer, canViewRates, dateFormat, timeFormat, getPayCodeStyle, currency]);
}